import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Camera, MountainSnow, Route as RouteIcon, Upload, X } from "lucide-react";
import { errorMessage, type Activity, type TripDetail, type TripPeak } from "@atlas/shared";
import { deleteTripRoute, setTripRoute, uploadTripRoute } from "../api/trips";
import { fetchActivities } from "../api/activities";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";
import { ConfirmDialog } from "./ConfirmDialog";
import { CheckinDialog } from "./PhotoCheckin";
import { Skel } from "./Skeleton";
import { formatTripDistance } from "../lib/trip-format";

// Planned route (spec 38 / ATL-160). The recordings stay the trip's line; a
// planned route only fills the gaps between two recordings that both sit on
// it. Filled stretches count like a recording (trails, peaks, landmarks,
// regions, the exploration map; Jack 2026-10-27). Summits still listed here are
// ones the fill did not collect (free plan's 12-month window, a removed match):
// a photo check-in collects them.

const iconButton: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--text-muted)",
  padding: 4,
  display: "inline-flex",
  borderRadius: "var(--radius-sm)",
};

const SOURCE_LABELS: Record<string, string> = {
  strava: "Strava",
  komoot: "Komoot",
  apple_health: "Apple Health",
  health_connect: "Health Connect",
  garmin: "Garmin",
  gpx: "GPX",
};

function invalidateAfterRouteChange(queryClient: ReturnType<typeof useQueryClient>) {
  // The trip (fills, figures), trip lists and activities (a GPX used as the
  // route leaves the list; trail progress may move). Fills also draw on the
  // exploration map and collect peaks, landmarks and regions.
  void queryClient.invalidateQueries({ queryKey: ["trips"] });
  void queryClient.invalidateQueries({ queryKey: ["activities"] });
  for (const queryKey of [["map/heatmap"], ["map"], ["collections"], ["profile/stats"]]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

function RoutePickerDialog({
  tripId,
  memberIds,
  onClose,
}: {
  tripId: string;
  memberIds: Set<string>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const gpx = useQuery({ queryKey: ["activities", { routePicker: "gpx" }], queryFn: () => fetchActivities(40, 0, "gpx") });
  const recent = useQuery({ queryKey: ["activities", { routePicker: "recent" }], queryFn: () => fetchActivities(40, 0) });
  const gpxList = gpx.data?.activities ?? [];
  const gpxIds = new Set(gpxList.map((a) => a.id));
  const others = (recent.data?.activities ?? []).filter((a) => !gpxIds.has(a.id));

  const choose = useMutation({
    mutationFn: (activityId: string) => setTripRoute(tripId, activityId),
    onSuccess: () => {
      invalidateAfterRouteChange(queryClient);
      onClose();
    },
  });
  const upload = useMutation({
    mutationFn: (file: File) => uploadTripRoute(tripId, file),
    onSuccess: () => {
      invalidateAfterRouteChange(queryClient);
      onClose();
    },
  });
  const pending = choose.isPending || upload.isPending;
  const error = choose.error ?? upload.error;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, onClose]);

  const row = (a: Activity) => {
    const on = selected === a.id;
    return (
      <label
        key={a.id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px",
          borderRadius: "var(--radius-md)",
          border: `1px solid ${on ? "var(--border-gold)" : "var(--border-subtle)"}`,
          background: on ? "var(--accent-soft)" : "var(--surface-sunken)",
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        <input
          type="radio"
          name="trip-route"
          checked={on}
          onChange={() => setSelected(a.id)}
          style={{ accentColor: "var(--gold-500)" }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: 13.5,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {a.name}
          </span>
          <span className="tnum" style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
            {SOURCE_LABELS[a.sourceType] ?? a.sourceType} ·{" "}
            {new Date(a.startedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            {a.distanceM != null ? ` · ${formatTripDistance(a.distanceM)}` : ""}
            {memberIds.has(a.id) ? " · in this trip" : ""}
          </span>
        </span>
      </label>
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Fill gaps from a planned route"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 24,
          maxWidth: 540,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          boxShadow: "var(--shadow-xl)",
          minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Choose a planned route</h2>
          <button type="button" aria-label="Close" style={iconButton} onClick={onClose} disabled={pending}>
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)" }}>
          Your recordings stay the trip's line. The route only fills gaps between two recordings that both sit on it,
          and the activity you pick leaves your list so it isn't counted twice.
        </p>
        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, minHeight: 0 }}>
          {gpx.isLoading || recent.isLoading ? (
            [0, 1, 2].map((i) => <Skel key={i} height="48px" />)
          ) : (
            <>
              <div className="eyebrow" style={{ fontSize: 10.5, margin: "2px 0 4px" }}>
                GPX uploads
              </div>
              {gpxList.length === 0 ? (
                <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--text-muted)" }}>No GPX uploads yet.</p>
              ) : (
                gpxList.map(row)
              )}
              {others.length > 0 && (
                <>
                  <div className="eyebrow" style={{ fontSize: 10.5, margin: "12px 0 4px" }}>
                    Other recent activities
                  </div>
                  {others.map(row)}
                </>
              )}
            </>
          )}
        </div>
        {error && (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--status-danger)" }}>
            {errorMessage(error, "Couldn't use that route. Try again.")}
          </p>
        )}
        <div className="r-wrap" style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Upload size={13} />}
            disabled={pending}
            onClick={() => fileRef.current?.click()}
          >
            {upload.isPending ? "Uploading…" : "Upload a GPX"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".gpx,application/gpx+xml"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) upload.mutate(f);
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RouteIcon size={13} />}
              disabled={!selected || pending}
              onClick={() => selected && choose.mutate(selected)}
            >
              {choose.isPending ? "Filling gaps…" : "Use as route"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Summits on filled stretches that need a photo to count. */
function RoutePeaks({ peaks }: { peaks: TripPeak[] }) {
  const queryClient = useQueryClient();
  const [checkingIn, setCheckingIn] = React.useState<TripPeak | null>(null);
  if (peaks.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
      <div className="eyebrow" style={{ fontSize: 10.5 }}>
        Your route passes these summits
      </div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
        Not collected from your route yet. A photo from the top collects a summit.
      </p>
      {peaks.map((p) => (
        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", minWidth: 0 }}>
          <MountainSnow size={15} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
          <Link
            to="/peaks/$slug"
            params={{ slug: p.slug }}
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              color: "var(--text-primary)",
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {p.name}
            {p.elevationM != null && (
              <span className="tnum" style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
                {" "}
                · {Math.round(p.elevationM).toLocaleString("en-GB")} m
              </span>
            )}
          </Link>
          <Button variant="ghost" size="sm" leftIcon={<Camera size={13} />} onClick={() => setCheckingIn(p)}>
            Check in with a photo
          </Button>
        </div>
      ))}
      {checkingIn && (
        <CheckinDialog
          target={{
            kind: "peak",
            slug: checkingIn.slug,
            name: checkingIn.name,
            latitude: checkingIn.latitude,
            longitude: checkingIn.longitude,
          }}
          onClose={() => setCheckingIn(null)}
          onSuccess={() => {
            setCheckingIn(null);
            void queryClient.invalidateQueries({ queryKey: ["trips"] });
          }}
        />
      )}
    </div>
  );
}

export function TripRoutePanel({
  trip,
  memberIds,
  routePeaks,
}: {
  trip: TripDetail;
  memberIds: Set<string>;
  routePeaks: TripPeak[];
}) {
  const queryClient = useQueryClient();
  const [picking, setPicking] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const remove = useMutation({
    mutationFn: () => deleteTripRoute(trip.id),
    onSuccess: () => {
      invalidateAfterRouteChange(queryClient);
      setConfirmRemove(false);
    },
  });
  const route = trip.route;

  return (
    <AtlasPanel
      title="Planned route"
      action={
        route ? (
          <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(true)}>
            Remove route
          </Button>
        ) : undefined
      }
    >
      {!route ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "var(--text-secondary)" }}>
            Forgot to start the watch? Add the route you planned. Your recordings stay the line, and only the gaps
            between them that sit on the route are filled.
          </p>
          <Button variant="secondary" size="sm" leftIcon={<RouteIcon size={13} />} onClick={() => setPicking(true)}>
            Fill gaps from a planned route
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <RouteIcon size={15} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--text-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {route.name}
            </span>
            <span className="tnum" style={{ fontSize: 12.5, color: "var(--text-muted)", flexShrink: 0 }}>
              {formatTripDistance(route.distanceM)}
            </span>
          </div>
          {route.status === "pending" && (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Working out the gaps…</p>
          )}
          {route.status === "ready" && (
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)" }}>
              {trip.filledM > 0
                ? `${formatTripDistance(trip.filledM)} filled from this route.`
                : "Nothing to fill: no gap between your recordings sits on this route."}
              {route.coveragePct != null ? ` Your recordings cover ${Math.round(route.coveragePct)}% of it.` : ""}
            </p>
          )}
          {route.status === "refused" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--status-warning)" }}>
                {route.refusedReason ?? "Atlas couldn't use this route to fill any gaps."}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
                Choose another route
              </Button>
            </div>
          )}
          <RoutePeaks peaks={routePeaks} />
        </div>
      )}

      {picking && <RoutePickerDialog tripId={trip.id} memberIds={memberIds} onClose={() => setPicking(false)} />}
      {confirmRemove && (
        <ConfirmDialog
          title="Remove the planned route?"
          body="The filled stretches leave the map and the distance. Trail progress they already earned stays."
          confirmLabel="Remove route"
          pendingLabel="Removing…"
          danger
          pending={remove.isPending}
          error={remove.isError ? errorMessage(remove.error, "Couldn't remove the route. Try again.") : null}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirmRemove(false)}
        />
      )}
    </AtlasPanel>
  );
}
